import React from 'react';
import { Navigate } from 'react-router-dom';
import { useModules } from '../../hooks/useModules';

interface ModuleGateProps {
  slug: string;
  children: React.ReactNode;
}

/**
 * Route gate for module-controlled pages.
 *
 * Renders children only when the module is "ready for use" (status `active`).
 * Modules with status `development` or `maintenance` redirect to home — they
 * appear in the sidebar (rendered with "Em breve" badge + disabled click), but
 * direct-URL access must not bypass that gate. `hidden` modules are filtered
 * out of the visibility list entirely by the backend.
 *
 * To enable a module post-launch, flip status to `active` via AdminModulesPage
 * — no deploy required.
 */
const ModuleGate: React.FC<ModuleGateProps> = ({ slug, children }) => {
  const { isModuleVisible, getModuleStatus, loaded } = useModules();

  if (!loaded) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-[#7063f7]" />
      </div>
    );
  }

  // Defense in depth: when /api/modules is down or returned an empty list,
  // `useModules` falls back to the permissive isModuleVisible=true path. That
  // by itself does not enforce the development/maintenance gate, because
  // `getModuleStatus` returns null and `null !== 'development'` is true.
  // For gated routes we fail closed: only render when the backend explicitly
  // confirms status=active.
  const status = getModuleStatus(slug);
  const isAccessible = isModuleVisible(slug) && status === 'active';

  if (!isAccessible) {
    return <Navigate to="/" replace />;
  }

  return <>{children}</>;
};

export default ModuleGate;
